/**
 * BiomechanicsBreakdown Component
 *
 * Displays per-pitch-type biomechanical signatures including
 * release point, arm slot variance, glove position, and timing.
 *
 * @module components/prediction/BiomechanicsBreakdown
 */

import React, { useState } from 'react';
import {
  Target,
  Clock,
  Move,
  Activity,
  ChevronRight,
  AlertTriangle,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { PITCH_TYPE_LABELS } from '../../lib/predictionService';

// ═══════════════════════════════════════════════════════════════════════════
// PITCH TYPE COLORS
// ═══════════════════════════════════════════════════════════════════════════

const PITCH_TYPE_COLORS = {
  FB: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  SI: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
  CT: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  CB: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
  SL: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  CH: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
  SP: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30' },
  KC: { bg: 'bg-pink-500/20', text: 'text-pink-400', border: 'border-pink-500/30' }
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Metric card for individual biomechanical measurements
 */
function MetricCard({ icon: Icon, label, value, unit, trend, variance }) {
  const hasTrend = trend !== undefined && trend !== null;
  const TrendIcon = trend > 0 ? TrendingUp : TrendingDown;
  const trendColor = trend > 0 ? 'text-green-400' : 'text-red-400';

  return (
    <div className="bg-white/5 rounded-lg p-4 border border-white/[0.08]">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-gray-500" />
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-white">{value}</span>
        {unit && <span className="text-sm text-gray-400">{unit}</span>}
      </div>
      {(hasTrend || variance) && (
        <div className="flex items-center gap-3 mt-2 text-xs">
          {hasTrend && (
            <div className={`flex items-center gap-1 ${trendColor}`}>
              <TrendIcon className="w-3 h-3" />
              <span>{Math.abs(trend).toFixed(1)}%</span>
            </div>
          )}
          {variance && (
            <div className="text-gray-500">
              ±{variance}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Variance indicator bar
 */
function VarianceBar({ value, maxValue = 10, label }) {
  const percentage = Math.min((Math.abs(value) / maxValue) * 100, 100);
  const isHigh = percentage >= 60;
  const isMedium = percentage >= 30 && percentage < 60;
  const barColor = isHigh ? 'bg-red-500' : isMedium ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">{label}</span>
        <span className={`font-medium ${isHigh ? 'text-red-400' : isMedium ? 'text-yellow-400' : 'text-green-400'}`}>
          {value.toFixed(1)}°
        </span>
      </div>
      <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Pitch type card showing all biomechanical signatures
 */
function PitchTypeCard({ pitchType, signature, isExpanded, onToggle }) {
  const colors = PITCH_TYPE_COLORS[pitchType] || PITCH_TYPE_COLORS.FB;
  const label = PITCH_TYPE_LABELS[pitchType] || pitchType;

  // Extract signature values with defaults
  const releasePoint = signature?.releasePoint || { x: 0, y: 0, z: 0 };
  const armSlot = signature?.armSlot || { average: 0, variance: 0 };
  const glovePosition = signature?.glovePosition || { height: 0, variance: 0 };
  const timing = signature?.timing || { average: 0, variance: 0 };
  const sampleCount = signature?.sampleCount || 0;
  const tippingIndicators = signature?.tippingIndicators || [];

  return (
    <div className={`bg-black border ${colors.border} rounded-xl overflow-hidden transition-all duration-300`}>
      {/* Header - Always visible */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-lg text-sm font-bold ${colors.bg} ${colors.text}`}>
            {pitchType}
          </span>
          <span className="font-semibold text-white">{label}</span>
          <span className="text-sm text-gray-500">({sampleCount} samples)</span>
        </div>
        <div className="flex items-center gap-3">
          {tippingIndicators.length > 0 && (
            <div className="flex items-center gap-1 text-orange-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-medium">{tippingIndicators.length} tells</span>
            </div>
          )}
          <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-white/[0.08]">
          {/* Metrics grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
            <MetricCard
              icon={Target}
              label="Release Height"
              value={releasePoint.y?.toFixed(1) || '6.0'}
              unit="ft"
              variance={signature?.releaseVariance?.toFixed(2)}
            />
            <MetricCard
              icon={Activity}
              label="Arm Slot"
              value={armSlot.average?.toFixed(1) || '45'}
              unit="°"
              variance={`${armSlot.variance?.toFixed(1) || '0'}°`}
            />
            <MetricCard
              icon={Move}
              label="Glove Height"
              value={glovePosition.height?.toFixed(1) || '3.5'}
              unit="ft"
              trend={glovePosition.trend}
            />
            <MetricCard
              icon={Clock}
              label="Timing"
              value={timing.average?.toFixed(2) || '1.35'}
              unit="sec"
              variance={`${timing.variance?.toFixed(2) || '0'}s`}
            />
          </div>

          {/* Variance breakdown */}
          <div className="bg-white/[0.02] rounded-lg p-4 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500">
              Variance Analysis
            </h4>
            <VarianceBar value={armSlot.variance || 0} maxValue={10} label="Arm Slot Variance" />
            <VarianceBar value={glovePosition.variance || 0} maxValue={5} label="Glove Position Variance" />
            <VarianceBar value={(timing.variance || 0) * 100} maxValue={20} label="Timing Variance" />
          </div>

          {/* Tipping indicators */}
          {tippingIndicators.length > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-orange-400 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Detected Tells
              </h4>
              <ul className="space-y-2">
                {tippingIndicators.map((indicator, idx) => (
                  <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">•</span>
                    <span>{indicator.description || indicator}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * BiomechanicsBreakdown - Per-pitch-type biomechanical analysis
 *
 * @param {Object} props
 * @param {Object} props.signatures - Object mapping pitch types to signature data
 * @param {string[]} props.pitchTypes - Array of pitch types to display
 * @param {string} props.selectedPitchType - Currently selected pitch type
 * @param {Function} props.onPitchTypeSelect - Callback when pitch type is selected
 */
export default function BiomechanicsBreakdown({
  signatures = {},
  pitchTypes = [],
  selectedPitchType = null,
  onPitchTypeSelect
}) {
  const [expandedTypes, setExpandedTypes] = useState(
    selectedPitchType ? [selectedPitchType] : []
  );

  // Toggle expansion
  const toggleExpanded = (pitchType) => {
    setExpandedTypes(prev => {
      if (prev.includes(pitchType)) {
        return prev.filter(t => t !== pitchType);
      }
      return [...prev, pitchType];
    });
    onPitchTypeSelect?.(pitchType);
  };

  // Expand all
  const expandAll = () => {
    setExpandedTypes(pitchTypes);
  };

  // Collapse all
  const collapseAll = () => {
    setExpandedTypes([]);
  };

  if (pitchTypes.length === 0) {
    return (
      <div className="bg-black border border-white/[0.08] rounded-xl p-8 text-center">
        <Activity className="w-12 h-12 mx-auto text-gray-500 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">No Biomechanical Data</h3>
        <p className="text-gray-400 text-sm">
          Biomechanical signatures will appear here after video analysis.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with expand/collapse controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Activity className="w-5 h-5" />
          <span className="font-semibold">Biomechanical Signatures</span>
          <span className="text-sm text-gray-500">({pitchTypes.length} pitch types)</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-white transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-white transition-colors"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Pitch type cards */}
      <div className="space-y-3">
        {pitchTypes.map(pitchType => (
          <PitchTypeCard
            key={pitchType}
            pitchType={pitchType}
            signature={signatures[pitchType]}
            isExpanded={expandedTypes.includes(pitchType)}
            onToggle={() => toggleExpanded(pitchType)}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border border-white/[0.08] rounded-lg bg-white/[0.02]">
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-green-500" />
            <span>Low Variance (Consistent)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-yellow-500" />
            <span>Medium Variance</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-red-500" />
            <span>High Variance (Potential Tell)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
